export type AutosaveStatus = 'saved' | 'unavailable';

/**
 * localStorage when it actually works (probed with a real write — Safari
 * private mode and some test environments expose it but throw or stub it),
 * otherwise an in-memory stand-in so the app still runs without autosave.
 * Writes are guarded so a quota overflow degrades autosave instead of
 * breaking store actions.
 */
export const createSafeStorage = (
  onStatus: (status: AutosaveStatus) => void
): Storage => {
  try {
    const ls = globalThis.localStorage;
    const probeKey = '__resolve_probe__';
    ls.setItem(probeKey, '1');
    ls.removeItem(probeKey);
    onStatus('saved');
    return {
      getItem: (key: string) => {
        try {
          return ls.getItem(key);
        } catch {
          onStatus('unavailable');
          return null;
        }
      },
      setItem: (key: string, value: string) => {
        try {
          ls.setItem(key, value);
          onStatus('saved');
        } catch {
          onStatus('unavailable');
        }
      },
      removeItem: (key: string) => ls.removeItem(key),
      clear: () => ls.clear(),
      key: (index: number) => ls.key(index),
      get length() {
        return ls.length;
      },
    } as Storage;
  } catch {
    onStatus('unavailable');
    const mem = new Map<string, string>();
    return {
      getItem: (key: string) => mem.get(key) ?? null,
      setItem: (key: string, value: string) => {
        mem.set(key, value);
      },
      removeItem: (key: string) => {
        mem.delete(key);
      },
      clear: () => {
        mem.clear();
      },
      key: (index: number) => Array.from(mem.keys())[index] ?? null,
      get length() {
        return mem.size;
      },
    } as Storage;
  }
};
